import type { AgentStateClass } from './AgentState'

import { Logger } from 'besonders-logger'
import { createStore, reconcile } from 'solid-js/store'
import { stateDB } from '../local-state'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

// A self-roster we own and publish to IPNS.
export interface IRoster {
	id: string // IPNS name (k51...) — W3Name.toString()
	pk: Uint8Array // Ed25519 private key, protobuf-encoded (same shape as IShare.pk)
	name: string
	createdAt: string // ISO timestamp
	isDeleted?: boolean
	lastPush: string | null
	lastCID?: string
	descriptionOverrides?: Record<string, string> // shareID -> custom description
	lastPayloadHash?: string // to skip no-op re-publish
	includedShareIDs?: string[] // opt-in allowlist: a share is published in this roster only if its id is listed here (see buildSelfRosterPayload)
}
// A doc we consume: a registry (URL) or another depot's self-roster (IPNS id).
export interface IRosterSource {
	id: string // stable id (the url for registry, the ipns id for self)
	url: string // registry JSON URL, OR an ipns id for kind 'self'
	kind: 'registry' | 'self'
	name?: string
	createdAt: string
	lastPull: string | null
	lastCID?: string
	discovered: Record<string, string> // shareID -> depotID (for grouped UI + de-dup)
}

// Module-scoped Solid stores so consumers in components react to mutations.
// (Replaces the reactivity that the old MobX→Solid external-source bridge gave us.)
const [_rosters, setRostersStore] = createStore<IRoster[]>([])
const [_rosterSources, setRosterSourcesStore] = createStore<IRosterSource[]>([])

export class AgentRoster {
	mixed = this as unknown as AgentStateClass
	get rosters(): IRoster[] { return _rosters }
	get rosterSources(): IRosterSource[] { return _rosterSources }

	get rostersMap() {
		return new Map(this.rosters.map(roster => [roster.id, roster]))
	}

	get rosterSourcesMap() {
		return new Map(this.rosterSources.map(source => [source.id, source]))
	}

	addRoster = async function (this: AgentStateClass, roster: IRoster) {
		LOG(`Adding roster:`, roster)
		// (i) dexie does some type checking, so we do it first & await
		await stateDB.rosters.add(roster)
		this.reloadRosters()
	}

	addRosterSource = async function (this: AgentStateClass, source: IRosterSource) {
		LOG(`Adding rosterSource:`, source)
		await stateDB.rosterSources.add(source)
		this.reloadRosters()
	}

	updateRoster = async function (this: AgentStateClass, id: string, changes: Partial<Omit<IRoster, 'id'>>) {
		const idx = _rosters.findIndex(p => p.id === id)
		if (idx === -1) throw ERROR(`[updateRoster] id not found:`, id, changes)
		LOG(`Updating roster:`, id, changes, _rosters[idx])
		await stateDB.rosters.update(id, changes)
		setRostersStore(idx, changes)
	}

	updateRosterSource = async function (this: AgentStateClass, id: string, changes: Partial<Omit<IRosterSource, 'id'>>) {
		const idx = _rosterSources.findIndex(p => p.id === id)
		if (idx === -1) throw ERROR(`[updateRosterSource] id not found:`, id, changes)
		LOG(`Updating rosterSource:`, id, changes, _rosterSources[idx])
		await stateDB.rosterSources.update(id, changes)
		setRosterSourcesStore(idx, changes)
	}

	deleteRoster = async function (this: AgentStateClass, rosterID: string) {
		const roster = this.rosters.find(p => p.id === rosterID)
		LOG(`Deleting roster:`, rosterID, roster)
		if (!roster) throw ERROR(`Trying to delete non-existing roster:`, rosterID)
		await stateDB.rosters.delete(rosterID)
		this.reloadRosters()
	}

	deleteRosterSource = async function (this: AgentStateClass, sourceID: string) {
		const source = this.rosterSources.find(p => p.id === sourceID)
		LOG(`Deleting rosterSource:`, sourceID, source)
		if (!source) throw ERROR(`Trying to delete non-existing rosterSource:`, sourceID)
		await stateDB.rosterSources.delete(sourceID)
		this.reloadRosters()
	}

	reloadRosters = async () => {
		const rosters = (await stateDB.rosters.orderBy('createdAt').toArray()).filter(r => !r.isDeleted)
		const rosterSources = await stateDB.rosterSources.orderBy('createdAt').toArray()
		// `reconcile` keeps stable identity for items already present (matched by id),
		// so <For> reuses existing rows instead of remounting on every reload.
		setRostersStore(reconcile(rosters, { key: 'id' }))
		setRosterSourcesStore(reconcile(rosterSources, { key: 'id' }))
		DEBUG(`Loaded from IDB:`, { rosters: this.rosters, rosterSources: this.rosterSources, stateDB })
	}
}
